
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import WebsiteGroupRepositoryInterface, { WebsiteGroup } from '@/services/WebsiteGroup/interfaces';
import { DataTableHeader } from 'vuetify';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import WebsiteMigrationsRepositoryInterface, {
  WebsiteMigration,
  WebsiteMigrationActionLog,
  WebsiteMigrationActionMessageInterface,
  WebsiteMigrationActionMessagePayloadInterface,
  WebsiteMigrationMessageInterface,
} from '@/services/WebsiteMigrations/interfaces';
import { Website } from '@/services/Website/interfaces';
import RetryMigrationDialog from '@/components/dialogs/website-migration/RetryMigrationDialog.vue';
import VComponent from '@/components/base/VComponent.vue';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'websiteGroupRepository',
    'serverRepository',
    'websiteMigrationsRepository',
  ],
  components: {
    VComponent,
    RetryMigrationDialog,
  },
  sockets: {
    websiteMigration(msg: WebsiteMigrationMessageInterface): void {
      this.$emit('migrationReceived', msg.payload);
    },
    websiteMigrationAction(msg: WebsiteMigrationActionMessageInterface): void {
      this.$emit('actionReceived', msg.payload);
    },
  },
})
export default class GroupMigrationList extends Vue {
  public loading: boolean = true;

  public websiteHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Domain', value: 'domain' },
    { text: 'Status', value: 'status' },
  ];

  public migrationHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Websites', value: 'ids' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public websiteGroupRepository!: WebsiteGroupRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public websiteMigrationsRepository!: WebsiteMigrationsRepositoryInterface;

  public sourceGroup: WebsiteGroup = {} as WebsiteGroup;
  public targetGroup: WebsiteGroup = {} as WebsiteGroup;

  public sourceServer: ServerInterface = {} as ServerInterface;
  public targetServer: ServerInterface = {} as ServerInterface;
  public servers: ServerInterface[] = [];
  public migrations: WebsiteMigration[] = [];

  public retryMigrationDialog: string | null = null;
  public migrationForRetryDialog: !WebsiteMigration;
  public sourceWebsiteForMigrationDialog!: Website;
  public sourceServerForMigrationDialog!: ServerInterface;
  public targetServerForMigrationDialog!: ServerInterface;
  public targetGroupForMigrationDialog!: WebsiteGroup;

  public async created(): Promise<void> {
    this.$on('migrationReceived', this.handleMigration);
    this.$on('migrationReceived', this.handleMigrationAction);

    try {
      await this.loadServers();
      await this.loadGroups();
      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
    }
  }

  public async loadGroups(): Promise<void> {
    if (!this.$route?.query?.sourceGroupId && !this.$route?.query?.targetGroupId) {
      throw new Error('Group Migration not found!');
    }

    await this.requestGroups();

    if (this.sourceGroup?.websites && this.sourceGroup.websites.length) {
      const mainWebsite = this.sourceGroup.websites.find((w) => w.id === this.sourceGroup?.mainWebsite);
      if (!mainWebsite) {
        return;
      }

      this.sourceServer = await this.serverRepository.getServer(mainWebsite.originServer);
    }

    if (this.targetGroup?.websites && this.targetGroup.websites.length) {
      const mainWebsite = this.targetGroup.websites.find((w) => w.id === this.targetGroup?.mainWebsite);
      if (!mainWebsite) {
        return;
      }

      this.targetServer = await this.serverRepository.getServer(mainWebsite.originServer);
    }

    this.migrations = await this.websiteMigrationsRepository.getMigrationsBySourceAndTargetGroups(
      this.sourceGroup.id,
      this.targetGroup.id,
    );

    this.sortMigrations();
  }

  public async requestGroups(): Promise<void> {
    try {
      if (this.$route?.query?.sourceGroupId) {
        this.sourceGroup = await this.websiteGroupRepository.getGroup(Number(this.$route.query.sourceGroupId));
      } else {
        this.sourceGroup.id = -1;
        this.sourceGroup.websites = [];
      }

      if (this.$route?.query?.targetGroupId) {
        this.targetGroup = await this.websiteGroupRepository.getGroup(Number(this.$route.query.targetGroupId));
      } else {
        this.targetGroup.id = -1;
        this.targetGroup.websites = [];
      }
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
    }
  }

  public async loadServers(): Promise<void> {
    try {
      this.servers = await this.serverRepository.getAllServers();
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }

  public getRowClass(groupMainWebsite: number, websiteId: number): string {
    return groupMainWebsite === websiteId ? 'success' : '';
  }

  public async viewMigration(migration: WebsiteMigration): Promise<void> {
    await this.$router.push({
      name: 'view_website_migration',
      params: { id: migration.id.toString() },
    });
  }

  public async handleMigration(migration: WebsiteMigration): Promise<void> {
    const migrationIndex = this.migrations.findIndex((m) => m.id === migration.id);
    const websiteFound = [...this.sourceGroup.websites, this.targetGroup.websites]
      .find((w: Website) => w.id === migration.sourceWebsiteId || w.id === migration.targetWebsiteId);

    if (migrationIndex === -1 && !websiteFound) {
      return;
    }

    if (migrationIndex === -1) {
      this.migrations.push(migration);
      this.sortMigrations();
      await this.requestGroups();

      return;
    }

    this.migrations[migrationIndex].status = migration.status;
    if (['done', 'failed'].includes(migration.status)) {
      const index = this.targetGroup.websites.findIndex((w) => w.id === migration.targetWebsiteId);
      if (index === -1) {
        return;
      }

      this.targetGroup.websites[index].status = migration.status;
    }

    this.sortMigrations();
  }

  public handleMigrationAction(action: WebsiteMigrationActionMessagePayloadInterface): void {
    this.migrations.forEach((migration, mIndex) => {
      migration.actions.forEach((migrationAction, aIndex) => {
        if (migrationAction.id !== action.id) {
          return;
        }

        this.migrations[mIndex].actions[aIndex].status = action.status;
        this.migrations[mIndex].actions[aIndex].logs = action.logs as unknown as WebsiteMigrationActionLog[];
      });
    });
  }

  public displayMigrationAlert(migration: WebsiteMigration): boolean {
    return migration.actions.some((ma) => ma.status === 'failed' && ma.logs?.length);
  }

  public shouldApproveAction(migration: WebsiteMigration): boolean {
    return migration.actions.some((ma) => ma.action.includes('approve') && ma.status === 'in progress');
  }

  public sortMigrations(): void {
    this.migrations = this.migrations.sort((ma, mb) => {
      const primaryComparison = ma.sourceWebsiteId - mb.sourceWebsiteId;

      if (primaryComparison !== 0) {
        return primaryComparison;
      }

      return ma.targetWebsiteId - mb.targetWebsiteId;
    });
  }

  public canRetry(migration: WebsiteMigration): boolean {
    return migration.status === 'failed' &&
      !this.targetGroup.websites.find((w) => w.id === migration.targetWebsiteId) &&
      !this.migrations.filter((m) => m.id !== migration.id)
        .find((m) => m.sourceWebsiteId === migration.sourceWebsiteId && m.status !== 'failed');
  }

  public showRetryDialog(migration: WebsiteMigration): void {
    if (!this.sourceServer) {
      this.$errorDialog.show(`Source server not found, please create the migration from scratch!`);

      return;
    }
    const sourceWebsite = this.sourceGroup.websites.find((w) => w.id === migration.sourceWebsiteId);
    if (!sourceWebsite) {
      this.$errorDialog.show(`Source website not found to retry migration.`);

      return;
    }

    this.sourceWebsiteForMigrationDialog = sourceWebsite;

    this.sourceServerForMigrationDialog = this.sourceServer;

    this.migrationForRetryDialog = migration;

    this.targetGroupForMigrationDialog = this.targetGroup;

    if (this.targetServer) {
      this.targetServerForMigrationDialog = this.targetServer;
    }

    this.retryMigrationDialog = 'retry-migration-dialog';
  }

  public async retryMigration(
    migration: WebsiteMigration,
    sourceWebsite: Website,
    sourceServer: ServerInterface,
    targetServer: ServerInterface,
    targetGroup?: WebsiteGroup,
  ): Promise<void> {
    if (!migration || !sourceWebsite || !targetServer) {
      this.$errorDialog.show('Missing migration data. Please try again');

      return;
    }

    try {
      await this.websiteMigrationsRepository.createMigration(
        sourceWebsite.id,
        this.sourceGroup.id,
        sourceServer._id,
        targetServer._id,
        targetGroup?.id || -1,
      );
      this.$successDialog.show(`Migration started successfully.`);
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
    }
  }
}
